import classNames from "classnames";
import { FormField } from "components/Forms/FormField";
import { RichText } from "components/RichText/RichText";
import { stripHtmlTags } from "helpers";
import { createLogger } from "logging";
import { useState } from "react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

import { HookFormError } from "./HookFormError";
import { HookFormField } from "./HookFormField";

const logger = createLogger("HookForm:RichText");

interface HookFormRichTextProps<TFieldValues extends FieldValues, TName extends Path<TFieldValues>> extends FormField {
  name: TName;
  control: Control<TFieldValues, unknown, TFieldValues>;
  maxLength?: number;
}

export const HookFormRichText = <TFieldValues extends FieldValues, TName extends Path<TFieldValues>>(
  props: HookFormRichTextProps<TFieldValues, TName>
) => {
  const { name, control, maxLength, contextHelp, label, required } = props;
  const [hasFocus, setHasFocus] = useState<boolean>(false);

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required,
        validate: maxLength
          ? (value) => {
              const len = stripHtmlTags(value ?? "").length;
              return len <= maxLength || `Max. ${maxLength} Zeichen (aktuell: ${len})`;
            }
          : undefined,
      }}
      render={({ field, fieldState }) => {
        const { onChange, onBlur, value } = field;

        const { isDirty, isTouched, error } = fieldState;
        const text = stripHtmlTags(value ?? "");
        const charCount = maxLength ? text.length : 0;
        const isOverLimit = maxLength ? charCount > maxLength : false;

        const className = classNames(
          isDirty ? "is-dirty" : "",
          isTouched ? "is-touched" : "",
          error ? "is-error" : "",
          required && (isDirty || isTouched) && !text ? "sbb-invalid" : "",
          isOverLimit ? "sbb-invalid" : ""
        );

        logger.debug(`${name}: state ->`, className);

        return (
          <HookFormField name={name} required={required} hasFocus={hasFocus} label={label} contextHelp={contextHelp}>
            <RichText
              name={name}
              className={className}
              required={required}
              value={value}
              onFocus={() => {
                logger.trace("onFocus(): ", name);
                setHasFocus(true);
              }}
              onBlur={() => {
                logger.trace("onBlur(): ", name);
                setHasFocus(false);
                onBlur();
              }}
              onChange={(value) => {
                logger.debug("onChange(): ", value);
                onChange(value);
              }}
            />
            {maxLength && (
              <div className={classNames("richtext-char-count", isOverLimit ? "is-error" : "")}>
                {charCount} / {maxLength}
              </div>
            )}
            <HookFormError error={error} />
          </HookFormField>
        );
      }}
    />
  );
};
